import React, { useEffect } from 'react';
import { Loader2, Delete } from 'lucide-react';

const KEYS = ['1','2','3','4','5','6','7','8','9','clear','0','back'];

export default function POSLockOverlay({
  open,
  reason,
  users,
  selectedEmail,
  setSelectedEmail,
  secret,
  setSecret,
  unlocking,
  onUnlock,
  onLogout,
}) {
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e) => {
      if (unlocking) return;

      const key = String(e.key || '');

      if (/^[0-9]$/.test(key)) {
        e.preventDefault();
        if (String(secret || '').length < 4) {
          setSecret(`${secret || ''}${key}`);
        }
        return;
      }

      if (key === 'Backspace') {
        e.preventDefault();
        setSecret(String(secret || '').slice(0, -1));
        return;
      }

      if (key === 'Delete' || key === 'Escape') {
        e.preventDefault();
        setSecret('');
        return;
      }

      if (key === 'Enter') {
        e.preventDefault();
        if (String(secret || '').length >= 4) {
          onUnlock?.();
        }
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, secret, unlocking, onUnlock, setSecret]);

  if (!open) return null;

  const title =
    reason === 'inactive'
      ? 'Terminal bloqueado'
      : 'POS bloqueado';

  const subtitle =
    reason === 'inactive'
      ? 'O POS foi bloqueado por segurança após 5 minutos sem atividade. Pode usar o teclado do computador ou o painel no ecrã.'
      : 'Introduza o PIN para voltar a abrir o POS. Pode usar o teclado do computador ou o painel no ecrã.';

  const masked = String(secret || '').slice(0, 4);

  const handleKey = (key) => {
    if (unlocking) return;

    if (key === 'clear') {
      setSecret('');
      return;
    }

    if (key === 'back') {
      setSecret(String(secret || '').slice(0, -1));
      return;
    }

    if (String(secret || '').length >= 4) return;
    setSecret(`${secret || ''}${key}`);
  };

  return (
    <div
      className="fixed inset-0 z-[140] flex items-center justify-center px-4"
      style={{ background: 'rgba(2, 6, 23, 0.82)', backdropFilter: 'blur(6px)' }}
    >
      <div
        className="w-full max-w-md rounded-3xl p-7 shadow-2xl"
        style={{ background: '#f8fafc', border: '1px solid #cbd5e1', boxShadow: '0 20px 60px rgba(15,23,42,0.35)' }}
      >
        <div className="mb-6 text-center">
          <p className="text-3xl font-bold text-slate-900">{title}</p>
          <p className="mt-2 text-sm leading-6 text-slate-600">{subtitle}</p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">
              Utilizador
            </label>
              <select
                value={selectedEmail}
                onChange={(e) => {
                  setSelectedEmail(e.target.value);
                  setSecret('');
                }}
                className="w-full rounded-2xl px-4 py-3 text-sm outline-none"
                style={{
                  background: '#ffffff',
                  border: '1px solid #cbd5e1',
                  color: '#0f172a',
                  WebkitTextFillColor: '#0f172a',
                  colorScheme: 'light',
                  opacity: 1,
                }}
              >
                {(users || []).map((u) => (
                  <option key={u.email || u.id} value={u.email}>
                    {u.name} {u.role ? `· ${u.role}` : ''}
                  </option>
                ))}
              </select>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">
              PIN
            </label>

            <div
              className="mb-4 flex items-center justify-center gap-4 rounded-2xl px-4 py-5"
              style={{ background: '#ffffff', border: '1px solid #cbd5e1' }}
            >
              {[0,1,2,3].map((i) => (
                <div
                  key={i}
                  className="h-4 w-4 rounded-full"
                  style={{
                    background: masked[i] ? '#16a34a' : '#cbd5e1',
                    transform: masked[i] ? 'scale(1)' : 'scale(0.9)',
                    transition: 'all .15s ease',
                  }}
                />
              ))}
            </div>

            <div className="grid grid-cols-3 gap-3 pt-1">
              {KEYS.map((key) => {
                if (key === 'clear') {
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => handleKey(key)}
                      className="rounded-2xl px-4 py-4 text-sm font-semibold"
                      style={{ background: '#ffffff', border: '1px solid #cbd5e1', color: '#334155' }}
                    >
                      Limpar
                    </button>
                  );
                }

                if (key === 'back') {
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => handleKey(key)}
                      className="flex items-center justify-center rounded-2xl px-4 py-4"
                      style={{ background: '#ffffff', border: '1px solid #cbd5e1', color: '#334155' }}
                    >
                      <Delete className="h-5 w-5" />
                    </button>
                  );
                }

                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => handleKey(key)}
                    className="rounded-2xl px-4 py-4 text-xl font-bold"
                    style={{ background: '#ffffff', border: '1px solid #cbd5e1', color: '#0f172a' }}
                  >
                    {key}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex flex-col gap-3 pt-4 sm:flex-row">
            <button
              onClick={onLogout}
              className="flex-1 rounded-2xl px-4 py-3 text-sm font-semibold"
              style={{
                background: '#ffffff',
                border: '1px solid #cbd5e1',
                color: '#334155',
              }}
            >
              Terminar sessão
            </button>

            <button
              onClick={onUnlock}
              disabled={unlocking || String(secret || '').length < 4}
              className="flex-1 rounded-2xl px-4 py-3 text-sm font-semibold text-white disabled:opacity-60"
              style={{
                background: 'linear-gradient(135deg,#22c55e,#16a34a)',
                border: '1px solid rgba(34,197,94,0.25)',
              }}
            >
              {unlocking ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  A desbloquear...
                </span>
              ) : (
                'Desbloquear POS'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
